import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../../components/design-system';
import { BillingDetailsCard } from '../../components/host/profile/BillingDetailsCard';
import { HostInvoicesList } from '../../components/host/HostInvoicesList';
import type { HostBillingDetails, Payment } from '../../types';

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export function Earnings() {
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState<HostBillingDetails | null>(null);

  useEffect(() => {
    if (!user) return;
    void supabase
      .from('host_billing_details')
      .select('*')
      .eq('host_id', user.id)
      .maybeSingle()
      .then(({ data }) => { if (data) setBilling(data as HostBillingDetails); });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // Every captured payment for this host. The traveler charge is `status`;
    // whether the host has actually been paid is `payout_status`.
    supabase
      .from('payments')
      .select('*')
      .eq('host_id', user.id)
      .eq('status', 'paid')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setPayments((data as Payment[]) ?? []);
        setLoading(false);
      });
  }, [user]);

  // Split by payout state: "paid out" is money that's actually been released to
  // the host; everything else (held/processing/failed) is still pending.
  const paidOut = payments.filter((p) => p.payout_status === 'paid');
  const pending = payments.filter((p) => p.payout_status !== 'paid');
  const paidOutTotal = paidOut.reduce((sum, p) => sum + p.host_payout_cents, 0);
  const pendingTotal = pending.reduce((sum, p) => sum + p.host_payout_cents, 0);
  const currency = payments[0]?.currency ?? 'eur';
  if (loading) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Earnings</h1>
          <p className="text-app-muted text-sm">Track your income from completed trips.</p>
        </div>

        {/* Billing details — the whole money story lives on this page. */}
        {user ? (
          <div className="mb-6">
            <BillingDetailsCard hostId={user.id} details={billing} onSaved={setBilling} />
          </div>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div className="bg-app-card rounded-xl border border-app-border p-5">
            <p className="text-xs text-app-muted uppercase tracking-wide">Paid out</p>
            <p className="text-3xl font-semibold text-gold mt-1">{formatCents(paidOutTotal, currency)}</p>
            <p className="text-xs text-app-muted mt-1">{paidOut.length} released trip{paidOut.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-app-card rounded-xl border border-app-border p-5">
            <p className="text-xs text-app-muted uppercase tracking-wide">Pending</p>
            <p className="text-3xl font-semibold text-app-fg mt-1">{formatCents(pendingTotal, currency)}</p>
            <p className="text-xs text-app-muted mt-1">
              {pending.length
                ? `Paid twice a month, once you send your recibo`
                : 'Nothing awaiting release'}
            </p>
          </div>
        </div>

        {user ? <HostInvoicesList hostId={user.id} /> : null}

        {payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gold/12 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-7 h-7 text-gold">
                <path d="M15.5 2A1.5 1.5 0 0014 3.5v13a1.5 1.5 0 001.5 1.5h1a1.5 1.5 0 001.5-1.5v-13A1.5 1.5 0 0016.5 2h-1zM9.5 6A1.5 1.5 0 008 7.5v9A1.5 1.5 0 009.5 18h1a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0010.5 6h-1zM3.5 10A1.5 1.5 0 002 11.5v5A1.5 1.5 0 003.5 18h1A1.5 1.5 0 006 16.5v-5A1.5 1.5 0 004.5 10h-1z" />
              </svg>
            </div>
            <p className="text-sm text-app-muted">No earnings yet. Completed trips will appear here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {payments.map((p) => {
              const released = p.payout_status === 'paid';
              return (
                <div key={p.id} className="bg-app-card rounded-xl border border-app-border p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-fg">
                      {p.trip_days} day{p.trip_days > 1 ? 's' : ''} trip
                    </p>
                    <p className="text-xs text-app-muted">
                      {new Date(p.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-semibold ${released ? 'text-emerald-500' : 'text-app-fg'}`}>
                      {formatCents(p.host_payout_cents, p.currency)}
                    </p>
                    <p className={`text-caption font-medium ${released ? 'text-emerald-500' : 'text-gold'}`}>
                      {released ? 'Paid out' : p.payout_status === 'invoiced' ? 'Invoice sent' : 'Pending'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Earnings;
